// Wave Q — authority lifecycle strip.
//
// The studio runs on a 7-stage pipeline:
//
//   Capture → Truth → Research → Proof → Draft → Queue → Published
//
// Without a visible spine, the operator has to mentally reconstruct
// "where am I in the loop?" every time. This strip puts the state of
// every stage on screen at once: count + active/blocked indicator +
// click-to-jump.
//
// Stages array shape:
//   { id, label, count, active?, blocked?, passive?, substatus? }
//
// Visual contract in studio.css under .studio-lifecycle-strip.
//
// Intentionally flat (no nested lists, no animations beyond hover):
// the lifecycle is the data, not the chrome.

const DEFAULT_STAGES = [
  { id: 'ideas', label: 'Capture' },
  { id: 'truth', label: 'Truth' },
  { id: 'research', label: 'Research' },
  { id: 'proof', label: 'Proof' },
  { id: 'create', label: 'Draft' },
  { id: 'queue', label: 'Queue' },
  { id: 'delivered', label: 'Published' },
]

export function LifecycleStrip({
  stages,
  counts = {},
  active,
  blocked = {},
  passive = {},
  substatus = {},
  onStageClick,
  className,
}) {
  // Allow callers to override the stage list (e.g. compose-only flows)
  // but default to the canonical 7.
  const list = stages || DEFAULT_STAGES

  return (
    <div
      className={['studio-lifecycle-strip', className]
        .filter(Boolean)
        .join(' ')}
      role="navigation"
      aria-label="Authority lifecycle"
    >
      {list.map((stage) => {
        const isActive = active === stage.id
        const isBlocked = !!blocked[stage.id]
        const isPassive = !!passive[stage.id]
        const count = counts[stage.id]
        const sub = substatus[stage.id]
        return (
          <button
            type="button"
            key={stage.id}
            className={[
              'studio-lifecycle-stage',
              isActive ? 'active' : '',
              isBlocked ? 'blocked' : '',
              isPassive ? 'passive' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={
              onStageClick ? () => onStageClick(stage.id, stage) : undefined
            }
            disabled={!onStageClick}
            aria-current={isActive ? 'step' : undefined}
          >
            <span className="studio-lifecycle-label">{stage.label}</span>
            <span className="studio-lifecycle-count">
              {typeof count === 'number' ? count : count || 0}
            </span>
            {sub ? (
              <span className="studio-lifecycle-substatus">{sub}</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
